/**
 * Modern Chatbot Configuration Component
 * Simplified orchestrator following KISS principles
 * Uses modular components for better maintainability
 */

import { useState, useEffect } from 'react';
import { toast } from "@/hooks/use-toast";
import { useTranslation } from 'react-i18next';
import { chatbotService, ChatbotConfig } from '@/services/chatbotService';
import { generateStandardEmbedCode, getTestPageUrl } from '../config/apiConfig';
import { Check, Clock, Edit3, Settings, Save } from 'lucide-react';
import { 
  ConfigurationLoading, 
  ConfigurationError 
} from './shared';
import {
  ConfigurationProgress,
  BasicSettings,
  AppearanceSettings,
  AIConfiguration,
  ConfigurationActions
} from './configuration';

export function ChatbotConfigWrapper() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(true); // Default to edit mode
  const { t } = useTranslation('business', { keyPrefix: 'communication' });
  const { t: tc } = useTranslation('common');
  
  // Default configuration
  const [config, setConfig] = useState<ChatbotConfig>({
    isEnabled: false,
    botName: "Wildlife Assistant",
    welcomeMessage: "Hello! I'm your wildlife assistant. How can I help you today?",
    primaryColor: "#2C5F2D",
    secondaryColor: "#F3F4F6",
    position: "bottom-right",
    autoOpen: false,
    autoOpenDelay: 5,
    collectUserInfo: true,
    requireEmail: false,
    systemPrompt: "You are a helpful wildlife assistant.",
    modelProvider: "huggingface",
    modelName: "mistralai/Mistral-7B-Instruct-v0.3"
  });

  useEffect(() => {
    loadConfig();
  }, []);

  // Show summary view for already completed configurations on first load
  useEffect(() => {
    if (!loading && isFullyConfigured && config.businessId) {
      setIsEditMode(false);
    }
  }, [loading]); // Only run when loading changes (first load)

  // Step completion logic
  const getStepCompletion = () => {
    return {
      step1: config.isEnabled,
      step2: Boolean(config.botName?.trim() && config.welcomeMessage?.trim()),
      step3: Boolean(config.primaryColor && config.position),
      step4: Boolean(config.systemPrompt?.trim() && config.systemPrompt.length > 50),
      step5: Boolean(config.businessId) // Has been saved
    };
  };

  const stepCompletion = getStepCompletion();
  
  // Calculate overall progress
  const completedSteps = Object.values(stepCompletion).filter(Boolean).length;
  const totalSteps = Object.keys(stepCompletion).length;
  const progressPercentage = Math.round((completedSteps / totalSteps) * 100);
  const isFullyConfigured = progressPercentage === 100;
  
  // Track if there are unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Update handler that tracks changes
  const handleConfigUpdate = (updates: Partial<ChatbotConfig>) => {
    setHasUnsavedChanges(true);
    updateConfig(updates);
  };

  // Save handler that clears unsaved changes flag
  const handleSave = async () => {
    await saveConfig();
    setHasUnsavedChanges(false);
  };

  // Manual toggle functions with save check
  const handleEditMode = () => setIsEditMode(true);
  const handleViewMode = () => {
    if (hasUnsavedChanges) {
      // Show warning toast
      toast({
        title: "Unsaved Changes",
        description: "Please save your changes before exiting edit mode.",
        variant: "destructive",
      });
      return;
    }
    setIsEditMode(false);
  };
  
  const loadConfig = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await chatbotService.getConfig();
      if (response.success) {
        setConfig(response.data);
      } else {
        setError(response.error || 'Failed to load configuration');
      }
    } catch (err) {
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    setError(null);
    
    try {
      const response = await chatbotService.updateConfig({
        ...config,
        modelProvider: "huggingface" as const,
      });
      
      if (response.success) {
        setConfig(response.data);
        toast({
          title: t('configurationSaved'),
          description: t('chatbotSettingsUpdated'),
        });
      } else {
        setError(response.error || 'Failed to save configuration');
        toast({
          title: t('saveFailed'),
          description: response.error || t('failedToSaveConfiguration'),
          variant: "destructive",
        });
      }
    } catch (err) {
      setError('Failed to save configuration');
      toast({
        title: t('saveFailed'), 
        description: t('failedToSaveConfiguration'),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (updates: Partial<ChatbotConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  const handleTest = () => {
    if (!config.isEnabled) {
      toast({
        title: t('enableChatbotFirst'),
        description: t('enableChatbotBeforeTesting'),
        variant: "destructive",
      });
      return;
    }

    if (!config.businessId) {
      toast({
        title: t('saveConfigurationFirst'),
        description: t('saveBeforeTesting'),
        variant: "destructive",
      });
      return;
    }
    
    const testUrl = getTestPageUrl(config.businessId);
    window.open(testUrl, '_blank');
  };

  // Step indicator component
  const StepIndicator = ({ stepNumber, isComplete, color }: { stepNumber: number, isComplete: boolean, color: string }) => (
    <div className={`w-8 h-8 rounded-full font-bold text-sm flex items-center justify-center transition-all duration-200 ${
      isComplete 
        ? `bg-green-500 text-white shadow-md` 
        : `${color} shadow-sm`
    }`}>
      {isComplete ? <Check className="w-4 h-4" /> : stepNumber}
    </div>
  );

  // Loading state
  if (loading) {
    return <ConfigurationLoading />;
  }

  // Error state
  if (error) {
    return <ConfigurationError error={error} onRetry={loadConfig} />;
  }

  return (
    <div className="space-y-6 p-6">
      {/* Overall Progress Header */}
      <div className={`border rounded-xl p-4 transition-all duration-300 ${
        isFullyConfigured && !isEditMode 
          ? "bg-gradient-to-r from-emerald-50 to-emerald-50 border-green-200/60" 
          : "bg-gradient-to-r from-emerald-50 to-purple-50 border-blue-200/60"
      }`}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold text-slate-900">
            {isFullyConfigured && !isEditMode ? "Chatbot Configuration" : "Chatbot Setup Progress"}
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-lg font-bold text-blue-600">{completedSteps}/{totalSteps} Complete</span>
            {isFullyConfigured && !isEditMode && (
              <button
                onClick={handleEditMode}
                className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700 text-white rounded-lg text-sm font-medium transition-colors duration-200 shadow-sm hover:shadow-md"
              >
                <Edit3 className="w-4 h-4" />
                Edit Settings
              </button>
            )}
            {isEditMode && isFullyConfigured && (
              <button
                onClick={handleViewMode}
                className={`flex items-center gap-2 px-3 py-2 text-white rounded-lg text-sm font-medium transition-all duration-200 shadow-sm hover:shadow-md ${
                  hasUnsavedChanges 
                    ? "bg-amber-500 hover:bg-amber-600" 
                    : "bg-emerald-600 hover:bg-emerald-700"
                }`}
              >
                {hasUnsavedChanges ? (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes First
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Done Editing
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        <div className="w-full bg-white rounded-full h-3 shadow-inner">
          <div 
            className={`h-3 rounded-full transition-all duration-500 ease-out shadow-sm ${
              isFullyConfigured && !isEditMode 
                ? "bg-gradient-to-r from-emerald-500 to-emerald-600" 
                : "bg-gradient-to-r from-emerald-500 to-purple-600"
            }`}
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        <p className="text-sm text-slate-600 mt-2">
          {isFullyConfigured && !isEditMode 
            ? "🎉 Your chatbot is fully configured and live! Click 'Edit Settings' to make changes." 
            : isEditMode && isFullyConfigured
            ? "✏️ Edit mode: Make any changes you need, then click 'Done Editing' when finished."
            : "Follow the steps below to set up your AI chatbot"}
        </p>
      </div>
      {/* Step 1: Basic Settings */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 mb-4">
          <StepIndicator stepNumber={1} isComplete={stepCompletion.step2} color="bg-emerald-100 text-emerald-700" />
          <h3 className="text-lg font-semibold text-slate-900">Basic Settings</h3>
          <span className="text-sm text-slate-500">Required</span>
          {stepCompletion.step2 && <span className="text-sm text-green-600 font-medium">✓ Complete</span>}
        </div>
        {(isEditMode || !isFullyConfigured) ? (
          <BasicSettings config={config} onUpdate={handleConfigUpdate} />
        ) : (
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm text-slate-600">
              <strong>Bot Name:</strong> {config.botName} • <strong>Welcome Message:</strong> {config.welcomeMessage?.substring(0, 50)}...
            </p>
          </div>
        )}
      </div>

      {/* Step 2: Appearance & Behavior */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 mb-4">
          <StepIndicator stepNumber={2} isComplete={stepCompletion.step3} color="bg-purple-100 text-purple-700" />
          <h3 className="text-lg font-semibold text-slate-900">Appearance &amp; Behavior</h3>
          <span className="text-sm text-slate-500">Customize experience</span>
          {stepCompletion.step3 && <span className="text-sm text-green-600 font-medium">✓ Complete</span>}
        </div>
        {(isEditMode || !isFullyConfigured) ? (
          <AppearanceSettings config={config} onUpdate={handleConfigUpdate} />
        ) : (
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm text-slate-600">
              <strong>Brand Color:</strong> {config.primaryColor} • <strong>Position:</strong> {config.position}
            </p>
          </div>
        )}
      </div>

      {/* Step 3: AI Knowledge & Personality */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 mb-4">
          <StepIndicator stepNumber={3} isComplete={stepCompletion.step4} color="bg-orange-100 text-orange-700" />
          <h3 className="text-lg font-semibold text-slate-900">AI Knowledge &amp; Personality</h3>
          <span className="text-sm text-orange-600 font-medium">Most important for success!</span>
          {stepCompletion.step4 && <span className="text-sm text-green-600 font-medium">✓ Complete</span>}
        </div>
        {(isEditMode || !isFullyConfigured) ? (
          <AIConfiguration config={config} onUpdate={handleConfigUpdate} />
        ) : (
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm text-slate-600">
              <strong>System Prompt:</strong> {config.systemPrompt?.substring(0, 100)}...
            </p>
          </div>
        )}
      </div>

      {/* Step 4: Test & Save */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 mb-4">
          <StepIndicator stepNumber={4} isComplete={stepCompletion.step5} color="bg-blue-100 text-blue-700" />
          <h3 className="text-lg font-semibold text-slate-900">Test &amp; Save Configuration</h3>
          <span className="text-sm text-slate-500">Test your chatbot and save configuration</span>
          {stepCompletion.step5 && <span className="text-sm text-green-600 font-medium">✓ Saved</span>}
        </div>
        {(isEditMode || !isFullyConfigured) && (
          <ConfigurationActions
            onSave={handleSave}
            onTest={handleTest}
            saving={saving}
            canTest={config.isEnabled}
          />
        )}
      </div>

      {/* Step 5: Enable Your Chatbot */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 mb-4">
          <StepIndicator stepNumber={5} isComplete={stepCompletion.step1} color="bg-green-100 text-green-700" />
          <h3 className="text-lg font-semibold text-slate-900">Enable Your Chatbot</h3>
          <span className="text-sm text-green-600 font-medium">Final step - Go live!</span>
          {stepCompletion.step1 && <span className="text-sm text-green-600 font-medium">✓ Live</span>}
        </div>
        <ConfigurationProgress 
          config={config} 
          onToggleEnabled={(enabled) => updateConfig({ isEnabled: enabled })} 
        />
      </div>
    </div>
  );
} 